import { Link } from "react-router-dom";

// The case workspace ships with a fixed-position rail on the right edge of
// the viewport. It's rendered both inside CaseView (where each in-page tab
// lives behind a `?tab=` URL param) AND on the Chat Search page (where it
// replaces the legacy "Back to case" button). Centralising the markup keeps
// the two surfaces visually identical and makes "active tab" wiring obvious.
export type CaseEdgeTab =
  | "chat"
  | "timeline"
  | "notes"
  | "docs"
  | "videos"
  | "members"
  | "chats";

interface Props {
  caseId: number;
  activeTab: CaseEdgeTab;
  // Surfaced as a small pill next to the Documents item so the user can see
  // the case's total document count at a glance from anywhere in the case.
  totalDocs?: number;
  // Optional members count, rendered as a pill on the Members tab so the
  // team size is visible from any pane.
  memberCount?: number;
  // Optional videos count, rendered as a pill on the Videos tab so the
  // size of the video library is visible from any pane.
  videoCount?: number;
}

// Each tab carries both a full label (used at desktop / tablet widths) and a
// shorter mobile label so the buttons can comfortably sit in a single
// row on a 360–414px phone without horizontal scrolling. The CSS picks
// which span to show based on the active media query.
const IN_PAGE_TABS: Array<{
  id: Exclude<CaseEdgeTab, "chats">;
  label: string;
  shortLabel: string;
}> = [
  { id: "chat", label: "Ask", shortLabel: "Ask" },
  { id: "timeline", label: "Timeline", shortLabel: "Timeline" },
  { id: "notes", label: "Notes", shortLabel: "Notes" },
  { id: "docs", label: "Documents", shortLabel: "Docs" },
  { id: "videos", label: "Videos", shortLabel: "Videos" },
  { id: "members", label: "Members", shortLabel: "Team" },
];

export default function CaseEdgeRail({
  caseId,
  activeTab,
  totalDocs,
  memberCount,
  videoCount,
}: Props) {
  return (
    <nav className="case-view__edge-rail" role="tablist" aria-label="Case workspace sections">
      {IN_PAGE_TABS.map((t) => {
        const active = activeTab === t.id;
        // "chat" is the implicit default tab on /cases/:id, so we omit the
        // ?tab= param for it to keep the URL bar tidy.
        const to = t.id === "chat" ? `/cases/${caseId}` : `/cases/${caseId}?tab=${t.id}`;
        return (
          <Link
            key={t.id}
            to={to}
            role="tab"
            aria-selected={active}
            className={`case-view__edge-btn${active ? " case-view__edge-btn--active" : ""}`}
          >
            <span className="case-view__edge-btn-label">{t.label}</span>
            <span className="case-view__edge-btn-label case-view__edge-btn-label--short">
              {t.shortLabel}
            </span>
            {t.id === "docs" && typeof totalDocs === "number" && (
              <span className="case-view__edge-count">{totalDocs}</span>
            )}
            {t.id === "videos" && typeof videoCount === "number" && (
              <span className="case-view__edge-count">{videoCount}</span>
            )}
            {t.id === "members" && typeof memberCount === "number" && (
              <span className="case-view__edge-count">{memberCount}</span>
            )}
          </Link>
        );
      })}
      <Link
        to={`/cases/${caseId}/chats`}
        role="tab"
        aria-selected={activeTab === "chats"}
        className={`case-view__edge-btn${activeTab === "chats" ? " case-view__edge-btn--active" : ""}`}
        title="Search chats across the case with rich filters"
      >
        <span className="case-view__edge-btn-label">Chat Search</span>
        <span className="case-view__edge-btn-label case-view__edge-btn-label--short">
          Search
        </span>
      </Link>
    </nav>
  );
}
